<script setup>
import { computed, ref, watch } from "vue";
import { useModalogStore } from "@/stores/modalog";
import { useToastStore } from "@/stores/toast.js";
import { router, usePage } from "@inertiajs/vue3";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";
import * as Sentry from "@sentry/vue";
import useEmitter from "@/composables/useEmitter";

const modalogStore = useModalogStore();
const toastStore = useToastStore();

const props = defineProps({
    isOwner: {
        type: Boolean,
        default: false,
    },
    item: {
        type: Object,
        default: function () {
            return {};
        },
    },
    listUuid: {
        type: String,
        default: null,
    },
});

const emitter = useEmitter();

// STATE
const giftlistUuid = ref(null);
const submitting = ref(false);

// The host keeps this instance mounted between opens (stable-root
// pattern), so the selection re-seeds on every open (GH-955).
watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            giftlistUuid.value = null;
        }
    },
);

// COMPUTED
const buttonText = computed(() => {
    const moniker = listTypeMoniker.value ? `${listTypeMoniker.value} ` : "";

    return `${isMoveAction.value ? "Move" : "Copy"} to ${moniker}List`;
});

const busyButtonText = computed(() => {
    const moniker = listTypeMoniker.value ? `${listTypeMoniker.value} ` : "";

    return `${isMoveAction.value ? "Moving" : "Copying"} to ${moniker}List`;
});

const expertlists = computed(() => {
    return usePage().props.auth.user?.data?.expertlists.filter(
        (list) => props.listUuid !== list.uuid,
    );
});

const isItemDetail = computed(() => {
    return usePage().component === "Shared/ItemDetail";
});

const isMoveAction = computed(() => {
    return props.isOwner;
});

const wishlists = computed(() => {
    return usePage().props.auth.user?.data?.wishlists.filter(
        (list) => props.listUuid !== list.uuid,
    );
});

const listTypeMoniker = computed(() => {
    return !selectedWishList.value
        ? ""
        : selectedWishList.value?.type.charAt(0).toUpperCase() +
              selectedWishList.value?.type.substring(1);
});

const selectedWishList = computed(() => {
    return [...expertlists.value, ...wishlists.value].find(function (list) {
        return list.uuid === giftlistUuid.value;
    });
});

// METHODS
const actionMethod = () => {
    submitting.value = true;
    isMoveAction.value ? moveItemToWishList() : copyItemToWishList();
};

/**
 * Copy the item to another wish list.
 * @note If not list owner.
 */
const copyItemToWishList = () => {
    axios
        .post(route("item.copy", { item: props.item }), {
            item_uuid: props.item.uuid,
            list_uuid: giftlistUuid.value,
        })
        .then(() => {
            props.item.is_processing = true;
            toastStore.success(
                `"${props.item.name}" was copied to ${selectedWishList.value.name}.`,
            );
            props.item.is_processing_text = `${props.item.name} Copied to ${selectedWishList.value.name}`;

            setTimeout(() => {
                props.item.is_processing = false;
                props.item.is_processing_text = null;
            }, 3000);
        })
        .catch((error) => console.error)
        .finally(() => {
            submitting.value = false;
            modalogStore.closeModalog();
            emitter.emit("item-copied", {
                wishlistName: selectedWishList.value.name,
            });
        });
};

/**
 * Move's the item to another wish list.
 * @note If list owner only.
 */
const moveItemToWishList = () => {
    axios
        .put(route("item.move", { item: props.item }), {
            item_uuid: props.item.uuid,
            list_uuid: giftlistUuid.value,
        })
        .then(() => {
            toastStore.success(
                `"${props.item.name}" was moved to ${selectedWishList.value.name}.`,
            );

            /* On item detail views, set the wish list to the new wish list. */
            if (isItemDetail.value) {
                router.visit(route("item.detail", { item: props.item }));
            } else {
                props.item.is_processing = true;
                props.item.is_processing_text = `${props.item.name} Moved to ${selectedWishList.value.name}`;

                setTimeout(() => {
                    router.reload({ only: ["items"] });
                }, 1000);
            }
        })
        .catch((error) => {
            console.error(error);
            Sentry.captureException(error);
            toastStore.error(
                "There was an error moving the item. Please try again.",
            );
        })
        .finally(() => {
            submitting.value = false;
            modalogStore.closeModalog();
        });
};
</script>

<template>
    <BrandConfirmModalog
        :title="isMoveAction ? 'Move to Wish List' : 'Copy to Wish List'"
        :confirm-label="buttonText"
        :busy-label="busyButtonText"
        :busy="submitting"
        :confirm-disabled="!giftlistUuid"
        @confirm="actionMethod"
    >
        <div class="max-h-[320px] space-y-5 overflow-y-auto pr-1">
            <div v-if="wishlists?.length">
                <h3 class="text-[15px] font-bold text-[#3c3938]">
                    Your Wish Lists
                </h3>
                <ul
                    class="mt-2 divide-y divide-black/5 rounded-[5px] border border-solid border-[#e6dfdd]"
                >
                    <li v-for="list in wishlists" :key="list.uuid">
                        <label
                            class="flex cursor-pointer items-center gap-3 px-4 py-3 font-brand text-[15px] text-brand-navy transition hover:bg-brand-cream/40"
                            :for="list.uuid"
                        >
                            <input
                                type="radio"
                                class="size-4 shrink-0 accent-[#f21439]"
                                :id="list.uuid"
                                name="giftlist"
                                :value="list.uuid"
                                v-model="giftlistUuid"
                            />
                            {{ list.name }}
                        </label>
                    </li>
                </ul>
            </div>
            <div v-if="expertlists?.length">
                <h3 class="text-[15px] font-bold text-[#3c3938]">
                    Your Expert Lists
                </h3>
                <ul
                    class="mt-2 divide-y divide-black/5 rounded-[5px] border border-solid border-[#e6dfdd]"
                >
                    <li v-for="list in expertlists" :key="list.uuid">
                        <label
                            class="flex cursor-pointer items-center gap-3 px-4 py-3 font-brand text-[15px] text-brand-navy transition hover:bg-brand-cream/40"
                            :for="list.uuid"
                        >
                            <input
                                type="radio"
                                class="size-4 shrink-0 accent-[#f21439]"
                                :id="list.uuid"
                                name="giftlist"
                                :value="list.uuid"
                                v-model="giftlistUuid"
                            />
                            {{ list.name }}
                        </label>
                    </li>
                </ul>
            </div>
        </div>
    </BrandConfirmModalog>
</template>
